/**
 * Axios SWR Cache — Stale-While-Revalidate via IndexedDB
 *
 * Adds transparent caching to any axios call by adding a `cache` key to the config.
 * No imports needed in components — just add the parameter:
 *
 *   this.axios({
 *     url: router.generate('api_calendar_init'),
 *     cache: 'calendar_init',                    // ← this is all you need
 *   }).then((res) => {
 *     this.experiences = res.data.experiences;
 *   });
 *
 * How it works:
 *   1. On first visit (cold cache): behaves like a normal axios call.
 *      After the network responds, the response is stored in IndexedDB.
 *
 *   2. On return visits (warm cache): the .then() handler fires TWICE:
 *      - First, immediately (~5ms) with cached data from IndexedDB
 *      - Then again when the network responds with fresh data
 *      The handler is idempotent (just assigns to component state), so the
 *      second call silently overwrites with current data. The user sees the
 *      page render instantly and never notices the background refresh.
 *
 *   3. When no `cache` key is passed: axios behaves 100% normally. Zero overhead.
 *
 * Why IndexedDB instead of localStorage?
 *   localStorage has a ~5MB quota shared across all keys. A single manifest_init
 *   response can be 3MB+, causing QuotaExceededError. IndexedDB has no practical
 *   limit (browsers allow 100MB+ per origin).
 *
 * Cache TTL: entries expire after 30 days by default. Override per-call with
 *   `cacheTTL: 5 * 60 * 1000` (5 minutes) in the config object.
 *   Expired entries are treated as cache MISS — the network response is awaited.
 *
 * Setup: call wrapAxiosWithCache(axios) in app.js BEFORE Vue.use(vueAxios, wrappedAxios)
 *
 * ⚠️ IMPORTANT: Cached calls return a custom thenable, NOT a Promise.
 * Do NOT use `await` with cached axios calls — only `.then()` chains.
 * `await` resolves on the first `.then()` (cached data) and silently
 * discards the network refresh, so you'd never see fresh data.
 *
 * ⚠️ IMPORTANT: Only the function-call form supports caching.
 * `this.axios({ url, cache: 'key' })` ← SWR read + write
 * `this.axios.get(url, { cache: 'key' })` ← write only (shortcut bypasses SWR read path)
 * The shortcut methods (get/post/put/…) are bound to the raw axios instance and do not
 * go through the SWR wrapper. They will still write to IDB via the response interceptor,
 * but they will never serve a cached response. Always use the function-call form.
 */

const IDB_NAME = 'query_cache';
const IDB_STORE = 'cache';
const IDB_VERSION = 1;

// Recovery lever: if a release caches something bad (a malformed payload, or data
// scoped to the wrong account), bump this constant. On the next boot the mismatch
// against the stored version purges every entry — a deploy can fix a bad cache
// without waiting out DEFAULT_TTL_MS.
export const CACHE_SCHEMA_VERSION = 2;
const CACHE_SCHEMA_VERSION_KEY = 'query_cache_schema_version';

// Default TTL: 30 days. Cached entries older than this are treated as a MISS.
// Since SWR always revalidates in the background, a long TTL just means
// "show stale data while fetching" — the network response overwrites it every time.
// Components can override per-call with `cacheTTL: milliseconds` in the config.
const DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// Debug tracing: off by default everywhere, including prod. Opt in per-device with
// `localStorage.setItem('tw_cache_debug', '1')` so HIT/MISS/EXPIRED traces and network
// timing are available when support needs to diagnose a stale-data report in production.
const log = (key, msg, data) => {
  if (localStorage.getItem('tw_cache_debug') !== '1') return;
  const style = 'color: #7c3aed; font-weight: bold;';
  if (data !== undefined) {
    console.log(`%c[tw:cache:${key}] ${msg}`, style, data);
  } else {
    console.log(`%c[tw:cache:${key}] ${msg}`, style);
  }
};

// ---------------------------------------------------------------------------
// Identity scoping — makes every `cache:` key safe against cross-identity reuse
// ---------------------------------------------------------------------------
//
// The query_cache IndexedDB store is per-origin, so an unscoped key (e.g.
// 'app_init', 'manifest_init', 'dashboard_data_*', the CRM keys — ~30 call
// sites) is shared by every identity that visits the same origin in one
// browser. On a shared front-desk terminal or admin "Log in as" impersonation,
// one user then paints another's cached payload — leaking that identity's roles
// (which fires role-gated XHRs the live server 403s, TRIP-4333) or, across an
// account switch, another account's financial/booking data (TRIP-4306).
//
// Prefixing every key with the boot-time user-avatar id here — one place the
// wrapper owns on the way to IndexedDB — makes all existing and future `cache:`
// keys identity-safe by construction, with no per-call-site changes. The id is
// a global PK unique per (user × account), so it subsumes the account slug AND
// separates two users on the same account.
//
// Read cache-immune from #app[data-user-avatar-id] (server-rendered per request,
// the same source App.vue uses for currentUserAvatarId) — NOT from an app_init
// response, which would itself be cached and defeat the scoping.
//
// ⚠️ Snapshot ONCE at module load, not lazily per cache-op. Vue mounts with a
// render function (`new Vue(...).$mount('#app')` in app.js) which REPLACES the
// server-rendered `<div id="app" data-user-avatar-id="…">` with App.vue's own
// root `<div class="app-root">` — which carries none of those data-* attributes.
// So a `document.getElementById('app')` performed at cache-op time (any read or
// write, all of which happen AFTER mount) finds no attribute and returns null,
// collapsing every key to the bare unscoped bucket — the exact defeat QA caught
// on TRIP-4333 ("attribute correct in raw HTML, gone from the live DOM"). This
// module is required in app.js's boot path BEFORE $mount, so reading at module
// evaluation captures the attribute while it is still on the server-rendered div.
//
// Fail-open: when there is no identity (e.g. cross-tenant impersonation, where
// getUserAvatar() is null), keys are left unprefixed. A null avatar must never
// synthesize a shared bucket that a real identity could then read — it falls
// back to the bare key, and app_init itself is separately left uncached in that
// case (see App.vue appInitCacheOptions). See TRIP-4333 / TRIP-4306.
function readBootUserAvatarId() {
  if (typeof document === 'undefined') return null;
  const appElement = document.getElementById('app');
  return (appElement && appElement.dataset.userAvatarId) || null;
}

// Captured at module evaluation — before Vue mounts and strips the attribute.
// (app.js requires this module in its boot path ahead of `$mount('#app')`.)
let bootUserAvatarId = readBootUserAvatarId();

function getBootUserAvatarId() {
  return bootUserAvatarId;
}

/**
 * Re-read #app[data-user-avatar-id] and re-snapshot the boot identity.
 *
 * The identity is normally captured once at module load — before Vue mounts and
 * strips the server-rendered attribute — so there is no production reason to call
 * this. It exists for tests, which need to plant the attribute in jsdom AFTER the
 * module has already been imported (its module-load snapshot would otherwise be
 * null for the whole suite, masking the very scoping the tests assert).
 *
 * @returns {?string} the freshly captured identity
 */
export function __recaptureBootUserAvatarId() {
  bootUserAvatarId = readBootUserAvatarId();
  return bootUserAvatarId;
}

/**
 * Prefix a raw cache key with the boot-time identity, or return it unchanged
 * when no identity is resolved (fail-open — see block comment above). This is
 * the single source of truth for the on-disk key: the wrapper applies it on
 * both the read and write sides, and the offline-recovery direct read
 * (appInitOfflineFallback) applies it too, so write and read can't drift.
 *
 * @param {string} rawKey — the `cache:` value passed by the call site
 * @returns {string} the identity-scoped IndexedDB key
 */
export function scopedCacheKey(rawKey) {
  const identity = getBootUserAvatarId();
  return identity ? `${identity}:${rawKey}` : rawKey;
}

// ---------------------------------------------------------------------------
// IndexedDB helpers — opened once at module load, resolves in <5ms on return
// ---------------------------------------------------------------------------

// Cached connection promise. Eagerly opened at module load (below); reset to
// null (and lazily reopened by getDb) whenever a connection is closed or found
// unusable.
let dbPromise;

/**
 * Open (or reopen) the IndexedDB connection. Resolves to the IDBDatabase, or to
 * null if IndexedDB is unavailable / the open fails (fail open — caching simply
 * won't work). Never rejects.
 *
 * We register onclose/onversionchange handlers so that if the browser closes the
 * connection out from under us — mobile Safari/WebKit aggressively reclaims
 * IndexedDB connections on long-lived, backgrounded, or memory-pressured tabs —
 * we drop the cached handle. The next cache op then reopens a fresh connection
 * instead of forever throwing `InvalidStateError: ... the database connection is
 * closing.` off the stale handle (see TRIP-4588).
 */
function openDb() {
  const promise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(IDB_NAME, IDB_VERSION);
      req.onupgradeneeded = (e) => {
        e.target.result.createObjectStore(IDB_STORE);
      };
      req.onsuccess = (e) => {
        const db = e.target.result;
        // Drop the cached handle if this connection dies, but only if it's still
        // the current one (a newer reopen may have already replaced dbPromise).
        db.onclose = () => {
          if (dbPromise === promise) dbPromise = null;
        };
        // Another tab is upgrading the DB — close so we don't block it, and let
        // the next op reopen against the new version.
        db.onversionchange = () => {
          db.close();
          if (dbPromise === promise) dbPromise = null;
        };
        resolve(db);
      };
      req.onerror = () => resolve(null); // fail open — caching simply won't work
    } catch {
      resolve(null); // indexedDB.open() throws synchronously in sandboxed iframes
    }
  });
  return promise;
}

dbPromise = openDb();

const MEMORY_MAX_ENTRIES = 40;
const memoryCache = new Map();

function memoryGet(key, ttl) {
  const entry = memoryCache.get(key);
  if (!entry) return undefined;
  if (Date.now() - entry.ts > ttl) {
    memoryCache.delete(key);
    return undefined;
  }
  memoryCache.delete(key);
  memoryCache.set(key, entry);
  return structuredClone(entry.data);
}

function memorySet(key, data) {
  memoryCache.delete(key);
  memoryCache.set(key, { data: structuredClone(data), ts: Date.now() });
  while (memoryCache.size > MEMORY_MAX_ENTRIES) {
    memoryCache.delete(memoryCache.keys().next().value);
  }
}

export function isCacheWarm(rawKey, ttl = DEFAULT_TTL_MS) {
  const entry = memoryCache.get(scopedCacheKey(rawKey));
  return Boolean(entry) && Date.now() - entry.ts <= ttl;
}

export function warmCache(rawKey, ttl = DEFAULT_TTL_MS) {
  if (isCacheWarm(rawKey, ttl)) return Promise.resolve(true);
  const key = scopedCacheKey(rawKey);
  return idbGet(key, ttl).then((data) => {
    if (data === null || data === undefined) return false;
    memorySet(key, data);
    return true;
  });
}

export function peekCache(rawKey, ttl = DEFAULT_TTL_MS) {
  const data = memoryGet(scopedCacheKey(rawKey), ttl);
  return data === undefined ? null : data;
}

/** Get the cached connection promise, reopening if a prior one was discarded. */
function getDb() {
  if (!dbPromise) dbPromise = openDb();
  return dbPromise;
}

/**
 * Run a single IndexedDB request against the object store, hardened against a
 * browser-closed connection.
 *
 * `db.transaction()` throws synchronously (it is NOT delivered via onerror) once
 * the connection enters the "closing" state. We catch that, discard the stale
 * handle, and retry once against a freshly reopened connection. Any remaining
 * failure resolves to `undefined` — caching is best-effort and must never throw
 * an unhandled rejection into the app (TRIP-4588).
 *
 * @param {IDBTransactionMode} mode - 'readonly' | 'readwrite'
 * @param {(store: IDBObjectStore) => IDBRequest} buildRequest - builds the request
 * @returns {Promise<*>} the request's result, or undefined on any failure
 */
function runRequest(mode, buildRequest) {
  const attempt = (fromPromise, db, canRetry) => {
    if (!db) return Promise.resolve(undefined);
    let req;
    try {
      req = buildRequest(db.transaction(IDB_STORE, mode).objectStore(IDB_STORE));
    } catch {
      // Connection closed by the browser (InvalidStateError) or otherwise
      // unusable — drop the stale handle and retry once with a fresh one. Only
      // discard if it's still the current handle: a concurrent op racing the same
      // closing connection may have already reopened, and we should reuse that
      // rather than open yet another redundant connection.
      if (dbPromise === fromPromise) dbPromise = null;
      if (canRetry) {
        const retryPromise = getDb();
        return retryPromise.then((freshDb) => attempt(retryPromise, freshDb, false));
      }
      return Promise.resolve(undefined);
    }
    return new Promise((resolve) => {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(undefined);
    });
  };
  const currentPromise = getDb();
  return currentPromise.then((db) => attempt(currentPromise, db, true));
}

/**
 * Read a cache entry from IndexedDB, returning null if missing or expired.
 * @param {string} key - Cache key
 * @param {number} ttl - Max age in ms (entries older than this are treated as MISS)
 */
function idbGet(key, ttl) {
  return runRequest('readonly', (store) => store.get(key)).then((entry) => {
    if (!entry || !entry._ts) return null;
    const age = Date.now() - entry._ts;
    if (age > ttl) {
      log(key, `cache EXPIRED (age: ${Math.round(age / 1000)}s, ttl: ${Math.round(ttl / 1000)}s)`);
      return null;
    }
    return entry._data;
  });
}

/** Write a cache entry to IndexedDB with a timestamp + its TTL for sweeping. */
function idbSet(key, value, ttl) {
  const entry = { _data: value, _ts: Date.now(), _ttl: ttl || DEFAULT_TTL_MS };
  return runRequest('readwrite', (store) => store.put(entry, key)).then(() => undefined);
}

/**
 * Delete every entry past its OWN ttl. Storage is otherwise only reclaimed at
 * logout — and users routinely stay logged in for MONTHS — so without this the
 * per-trip cache (one entry per distinct trip viewed) grows unbounded. Async via
 * a cursor; never blocks. Each entry stores the ttl it was written with, so a
 * 7-day trip and a 30-day default each expire on their own schedule (TRIP-2666).
 */
export function sweepExpiredCache() {
  return getDb().then((db) => {
    if (!db) return;
    try {
      const now = Date.now();
      const req = db.transaction(IDB_STORE, 'readwrite').objectStore(IDB_STORE).openCursor();
      req.onsuccess = (e) => {
        const cursor = e.target.result;
        if (!cursor) return;
        const entry = cursor.value;
        const ttl = (entry && entry._ttl) || DEFAULT_TTL_MS;
        if (!entry || !entry._ts || now - entry._ts > ttl) cursor.delete();
        cursor.continue();
      };
    } catch (_e) {
      /* best-effort */
    }
  });
}

// ---------------------------------------------------------------------------
// Public API — flag management & cache clearing
// ---------------------------------------------------------------------------

/** Clear a specific cache entry (e.g. after a mutation that invalidates it) */
export function clearCache(key) {
  memoryCache.delete(key);
  memoryCache.delete(scopedCacheKey(key));
  return runRequest('readwrite', (store) => store.delete(key)).then(() => undefined);
}

/**
 * Read a cache entry directly. This direct read is the offline-fallback path
 * (e.g. serving the manifest init payload during an outage, TRIP-2666 AC2).
 */
export function readCache(key, ttl = DEFAULT_TTL_MS) {
  return idbGet(key, ttl);
}

/** Clear all cache entries */
export function clearAllCache() {
  memoryCache.clear();
  return runRequest('readwrite', (store) => store.clear()).then(() => undefined);
}

/**
 * Purge everything if CACHE_SCHEMA_VERSION was bumped since the last boot on this
 * device. This is the recovery lever for a bad release: bump the constant, ship it,
 * and every affected browser purges on its next load instead of waiting out the
 * 30-day TTL.
 */
function purgeIfSchemaVersionChanged() {
  const stored = localStorage.getItem(CACHE_SCHEMA_VERSION_KEY);
  if (stored === String(CACHE_SCHEMA_VERSION)) return;
  clearAllCache().then(() => {
    localStorage.setItem(CACHE_SCHEMA_VERSION_KEY, String(CACHE_SCHEMA_VERSION));
  });
}

// ---------------------------------------------------------------------------
// Interceptor installation — call once at boot
// ---------------------------------------------------------------------------

/**
 * Wrap an axios instance with the SWR cache layer.
 *
 * Call this BEFORE passing axios to vue-axios. vue-axios defines Vue.prototype.axios
 * as a non-configurable getter (can't be redefined later), so we wrap the instance
 * first and let vue-axios expose the wrapped version directly.
 *
 * The wrapper:
 *   - Checks for a `cache` property in the request config
 *   - If present, reads IndexedDB in parallel with the network
 *   - Returns a thenable that fires .then() handlers twice (cache, then network)
 *   - If absent, passes straight through to real axios (zero overhead)
 *
 * It also installs two standard axios interceptors on the real instance:
 *   - Request interceptor: moves `cache` to `_cacheKey` (so axios doesn't send it as a param)
 *   - Response interceptor: writes response.data to IndexedDB for cache keys
 *
 * Why can't we just use interceptors alone?
 *   Axios interceptors can't make .then() fire twice — they transform a single
 *   request/response. To deliver cached data BEFORE the network responds, we need
 *   to return a custom thenable that fires handlers on IDB read AND network response.
 *
 * Idempotent per raw instance: wrapping the same `axiosInstance` twice (e.g. app.js's
 * boot-time call and a plain module that also needs the cache) returns the SAME wrapped
 * function instead of installing a second set of interceptors.
 *
 * @param {Function} axiosInstance — the axios instance (import from 'axios')
 * @returns {Function} wrapped axios instance — pass this to Vue.use(vueAxios, wrappedAxios)
 */
const wrappedInstances = new WeakMap();

export function wrapAxiosWithCache(axiosInstance) {
  if (wrappedInstances.has(axiosInstance)) {
    return wrappedInstances.get(axiosInstance);
  }

  // Purge everything if this release bumped CACHE_SCHEMA_VERSION (recovery lever —
  // see clearAllCache), then prune expired entries. Sessions last months and only
  // an actual logout otherwise wipes the cache, so without the sweep the per-trip
  // cache would grow unbounded; sweeping at boot keeps it bounded by each entry's
  // TTL (TRIP-2666).
  purgeIfSchemaVersionChanged();
  sweepExpiredCache();

  // --- WRITE SIDE: store fresh network responses in IndexedDB ---
  axiosInstance.interceptors.response.use((response) => {
    const cacheKey = response.config?._cacheKey;
    if (cacheKey) {
      // Skip caching if Symfony redirected the request to the login page (session expired).
      // The global App.vue interceptor handles the redirect; we just must not cache the HTML.
      const redirectedToLogin = response.request?.responseURL?.includes('/login');
      if (redirectedToLogin) return response;

      const elapsed = response.config._cacheStartTime
        ? Math.round(performance.now() - response.config._cacheStartTime)
        : '?';
      log(cacheKey, `network complete in ${elapsed}ms`);
      idbSet(cacheKey, response.data, response.config._cacheTTL);
      if (response.config._cacheSync) memorySet(cacheKey, response.data);
    }
    return response;
  });

  // --- BOOKKEEPING: move cache key to internal prop before request fires ---
  // Without this, axios would include `cache` as a request parameter
  axiosInstance.interceptors.request.use((config) => {
    if (config.cache) {
      // Scope to the boot-time identity so the on-disk key matches the read side
      // (axiosWithCache below) and no two identities share a bucket. See scopedCacheKey.
      config._cacheKey = scopedCacheKey(config.cache);
      config._cacheTTL = config.cacheTTL; // preserve for the write-side per-entry TTL
      config._cacheSync = config.cacheSync;
      config._cacheStartTime = performance.now();
      delete config.cache;
      delete config.cacheTTL;
      delete config.cacheSync;
    }
    return config;
  });

  // --- READ SIDE: wrapper function that serves IDB data before network ---
  //
  // When config.cache is set, this returns a custom thenable instead of a real
  // Promise. The thenable fires .then() handlers twice:
  //   1. When IDB resolves (~5ms) — synthetic response with { data, fromCache: true }
  //   2. When network resolves — real axios response
  //
  // When config.cache is absent, this calls axios directly and returns a
  // normal Promise — completely transparent.
  const axiosWithCache = function axiosWithCache(config) {
    const rawKey = config && config.cache;

    // Fast path: no cache key — pass straight through
    if (!rawKey) {
      return axiosInstance(config);
    }

    // Scope the on-disk key to the boot-time identity so the read (idbGet below)
    // hits the same bucket the write side (request interceptor) stores under, and
    // no two identities share a cache entry. See scopedCacheKey.
    const cacheKey = scopedCacheKey(rawKey);

    // cacheTTL: per-call override for max cache age (default: 30 days)
    const ttl = config.cacheTTL || DEFAULT_TTL_MS;

    const startTime = performance.now();

    // Fire the real network request immediately (don't wait for IDB)
    const networkPromise = axiosInstance(config);

    // Return a thenable that fires handlers on both IDB and network resolution
    let networkResolved = false;

    return {
      _thenHandlers: [],
      _catchHandlers: [],
      _networkErrRegistered: false,

      then(onFulfilled, onRejected) {
        // NOTE: This thenable is not fully Promise/A+ compliant — .then() returns `this`
        // rather than a new Promise resolved with the handler's return value.
        // Chained transforms (.then(res => res.data).then(data => ...)) will NOT work:
        // the second handler receives the full response, not the transformed value.
        // All callers must use a single .then() that assigns state directly without
        // returning a value. Do NOT use `await` with cached calls (see module header).
        if (onFulfilled) this._thenHandlers.push(onFulfilled);
        if (onRejected) this._catchHandlers.push(onRejected);

        const memoryHit = config.cacheSync ? memoryGet(cacheKey, ttl) : undefined;
        if (memoryHit !== undefined) {
          log(cacheKey, 'cache HIT — serving from memory');
          const syntheticResponse = { data: memoryHit, fromCache: true, config };
          if (onFulfilled) {
            try {
              onFulfilled(syntheticResponse);
            } catch (e) {
              /* don't break chain */
            }
          }
        } else if (!this._idbRead) {
          this._idbRead = true;
          idbGet(cacheKey, ttl).then((cached) => {
            if (cached && config.cacheSync) memorySet(cacheKey, cached);
            if (cached && !networkResolved) {
              const elapsed = Math.round(performance.now() - startTime);
              log(cacheKey, `cache HIT — serving from IDB in ${elapsed}ms`);
              const syntheticResponse = { data: cached, fromCache: true, config };
              this._thenHandlers.forEach((handler) => {
                try {
                  handler(syntheticResponse);
                } catch (e) {
                  /* don't break chain */
                }
              });
            } else if (cached && networkResolved) {
              log(cacheKey, 'cache HIT but network already resolved — skipping stale data');
            } else {
              log(cacheKey, 'cache MISS — waiting for network');
            }
          });
        }

        // Network response fires handlers again with fresh data
        networkPromise
          .then((response) => {
            networkResolved = true;
            this._thenHandlers.forEach((handler) => {
              try {
                handler(response);
              } catch (e) {
                /* don't break chain */
              }
            });
          })
          .catch((error) => {
            this._catchHandlers.forEach((handler) => {
              try {
                handler(error);
              } catch (e) {
                /* don't break chain */
              }
            });
          });

        this._networkErrRegistered = true;
        return this;
      },

      catch(onRejected) {
        this._catchHandlers.push(onRejected);
        // Only register a networkPromise.catch if .then() hasn't already done so.
        // Without this guard, .then().catch() would fire the handler twice on error:
        // once from the .then()'s .catch() iterating _catchHandlers, and once directly here.
        if (!this._networkErrRegistered) {
          networkPromise.catch((error) => {
            this._catchHandlers.forEach((handler) => {
              try {
                handler(error);
              } catch (e) {
                /* don't break chain */
              }
            });
          });
          this._networkErrRegistered = true;
        }
        return this;
      },

      finally(onFinally) {
        // Fire after the NETWORK resolves (not after cache), since finally()
        // is typically used for loading spinners that should clear once.
        networkPromise
          .then(() => {
            try {
              onFinally();
            } catch (e) {
              /* don't break chain */
            }
          })
          .catch(() => {
            try {
              onFinally();
            } catch (e) {
              /* don't break chain */
            }
          });
        return this;
      },
    };
  };

  // --- Preserve the full axios API surface on the wrapper ---
  // Components use this.axios.CancelToken, this.axios.get(), etc.
  Object.keys(axiosInstance).forEach((key) => {
    axiosWithCache[key] = axiosInstance[key];
  });
  ['get', 'post', 'put', 'delete', 'patch', 'head', 'options', 'request'].forEach((method) => {
    if (axiosInstance[method]) {
      axiosWithCache[method] = axiosInstance[method].bind(axiosInstance);
    }
  });
  axiosWithCache.interceptors = axiosInstance.interceptors;
  axiosWithCache.defaults = axiosInstance.defaults;
  axiosWithCache.CancelToken = axiosInstance.CancelToken;

  wrappedInstances.set(axiosInstance, axiosWithCache);
  return axiosWithCache;
}
